import type { AccountOption } from "@app/components/account-combobox";
import KpiChart from "@app/components/kpi-chart";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import TxnTablePanel, { useTxnSort } from "@app/components/txn-table-panel";
import type { TxnSortColumn } from "@app/components/txn-table-panel";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import CreateRuleDialog from "@app/pages/transactions/components/create-rule-dialog";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { walletLedgerDialog_Query } from "@app/queries/walletLedgerDialog_Query.graphql";

import { CategoryCell, CounterpartyCell, NatureCell } from "./wallet-ledger-cells";
import type { CategoryOption } from "./wallet-ledger-cells";

const PAGE_SIZE = 100;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

export type ChartSeries = {
  name: string;
  points: ReadonlyArray<{ readonly month: string; readonly value: number | null | undefined }>;
  type: "line" | "bar";
  color?: string;
  stack?: string;
};

// The wallet's ledger at full size: the balance/flow chart it belongs under, plus
// the whole ledger — paginated, and editable cell by cell (counterparty, category,
// nature) rather than only through the bulk toolbar. Opened from the Actions menu
// so the wallet page itself stays a summary.
export default function WalletLedgerDialog({
  slug,
  name,
  series,
  onClose,
}: {
  slug: string;
  name: string;
  series: ChartSeries[];
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {/* Centred with inset + margin rather than a translate: a transformed
            ancestor becomes the containing block for position:fixed descendants,
            which would let this panel's overflow-hidden clip the cells' editing
            popovers. */}
        <Dialog.Content className="fixed inset-0 z-50 m-auto flex h-[92vh] w-[96vw] flex-col overflow-hidden rounded-lg bg-white shadow-2xl focus:outline-none">
          <div className="flex shrink-0 items-start justify-between border-b px-5 py-3">
            <div>
              <Dialog.Title className="font-bold text-gray-900">{name} ledger</Dialog.Title>
              <p className="text-xs text-gray-500">
                Every promoted {name} transaction. Click a counterparty, category or
                nature to change it — categories apply to the whole counterparty.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {series.length > 0 && series[0].points.length > 0 && (
            <div className="shrink-0 border-b px-5 py-3">
              <KpiChart
                key={series.map((s) => s.name).join("|")}
                title={`${name} balance (line) and monthly flows (bars)`}
                series={series}
                format="money"
                onMonthClick={() => {}}
              />
            </div>
          )}

          {/* The table owns the remaining height and scrolls inside it, so the
              chart stays pinned above it rather than scrolling away. */}
          <div className="flex min-h-0 flex-1 flex-col">
            <Suspense fallback={<p className="p-6 text-sm text-gray-400">Loading ledger…</p>}>
              <WalletLedger slug={slug} />
            </Suspense>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// The editable, paginated ledger for one wallet. One server-fetched page at a
// time (limit/offset on the shared transactions resolver), so a wallet with tens
// of thousands of rows opens as fast as an empty one.
function WalletLedger({ slug }: { slug: string }) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ruleSeed, setRuleSeed] = useState<string | null>(null);
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();
  const perspectiveId = getPerspectiveId();

  // A new sort re-orders the whole set server-side, so the old page number is
  // meaningless — go back to the first page.
  const sortAndReset = (column: TxnSortColumn) => {
    setPage(0);
    onSort(column);
  };

  const data = useLazyLoadQuery<walletLedgerDialog_Query>(
    txnsQuery,
    {
      accountNumber: slug,
      accountKind: "WALLET",
      perspectiveId: perspectiveId ?? undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      ...sortVars,
    },
    queryOptions
  );

  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] = data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));
  const categories: ReadonlyArray<CategoryOption> = data.categories;
  const categoriesById = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories]
  );

  const reload = () => refresh();

  const { busy: bulkBusy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId,
    onChanged: reload,
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  const exportParams: Record<string, string> = {
    account_number: slug,
    account_kind: "WALLET",
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  const cellProps = {
    busy: busy || bulkBusy,
    setBusy,
    onChanged: reload,
  };

  return (
    <>
      <TxnTablePanel
        mode="page"
        interaction="editable"
        title="Ledger"
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="No ledger activity yet."
        sort={sort}
        direction={direction}
        onSort={sortAndReset}
        exportParams={exportParams}
        busy={busy || bulkBusy || refreshing}
        renderBulkActions={renderBulkActions}
        pagination={{ page, pageSize: PAGE_SIZE, onPageChange: setPage }}
        renderCounterparty={(row) => (
          <CounterpartyCell
            row={row}
            stakeholders={stakeholderOptions}
            perspectiveId={data.perspective.id}
            onCreateRule={() => setRuleSeed(row.description)}
            {...cellProps}
          />
        )}
        renderCategory={(row) => (
          <CategoryCell
            row={row}
            categories={categories}
            categoriesById={categoriesById}
            perspectiveId={data.perspective.id}
            {...cellProps}
          />
        )}
        renderNature={(row) => (
          <NatureCell
            row={row}
            natures={data.expenseNatures}
            onCreateRule={() => setRuleSeed(row.description)}
            {...cellProps}
          />
        )}
      />
      {dialogs}
      {ruleSeed !== null && (
        <CreateRuleDialog
          description={ruleSeed}
          stakeholders={stakeholderOptions}
          accounts={accountOptions}
          natures={data.expenseNatures}
          onClose={() => setRuleSeed(null)}
          onApplied={() => {
            setRuleSeed(null);
            reload();
          }}
        />
      )}
    </>
  );
}

const txnsQuery = graphql`
  query walletLedgerDialog_Query(
    $accountNumber: String
    $accountKind: String
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
    $limit: Int
    $offset: Int
  ) {
    perspective(perspectiveId: $perspectiveId) {
      id
    }
    categories(perspectiveId: $perspectiveId) {
      id
      name
      parent {
        id
      }
    }
    transactions(
      accountNumber: $accountNumber
      accountKind: $accountKind
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
      limit: $limit
      offset: $offset
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          id
          name
        }
        category
        categoryNode {
          id
        }
        categorySliceCount
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;
